import _ from 'lodash';
import { getGroupChannelsUrlContainsStudentName, getSendbirdUserInfo } from '@apis/api/chat';
import { useChatImageMessageStore, useChatPopupStore, useChatStore } from '@store/chat';
import { useUserInfoQuery } from '@apis/query/user';
import { useQuery } from 'react-query';
import { HiddenChannelFilter } from '@sendbird/chat/groupChannel';
import { GroupChannelListProvider } from '@sendbird/uikit-react/GroupChannelList/context';
import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { UserType } from '@asset/enum/UserEnum';
import CustomGroupChannelList from './CustomGroupChannelList';
import { GroupChannelCustomType } from '@asset/type/chat';
import CustomGroupChannel from './GroupChannel/CustomGroupChannel';
import * as S from '@styles/chat/chat.style';
import { useWindowSize } from '@public/hooks/useWindowSize';
import { useKeyboardVisible } from 'hook/keyboard';
import CustomGroupChannelListSkeleton from './CustomGroupChannelListSkeleton';
import { CHAT_ROOM_REM_WIDTH, customStringSet, RESIZABLE_CHAT_ELEMENT_CLASSNAME } from 'constants/chat';
import { getUsernameFromNickname } from '@asset/function/chat';
import { GroupChannelProvider } from '@sendbird/uikit-react/GroupChannel/context';
import ChatPopupWrapper from './ChatPopupWrapper';
import ScheduledMessageSection from './scheduleMessage/ScheduledMessageSection';
import RndMediaWindow from '@components/common/RndMediaWindow';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const SendbirdProvider = dynamic(() => import('@sendbird/uikit-react/SendbirdProvider'), {
  ssr: false
});
function SendbirdWrapper() {
  const SEND_BIRD_APP_ID = process.env.NEXT_PUBLIC_SEND_BIRD_APP_ID;
  const {
    data: user
  } = useUserInfoQuery();
  const {
    selectedChannel,
    clickedChatNotification,
    setSelectedChannel
  } = useChatStore();
  const {
    isChatPopupOpen,
    chatPopupRef: storeChatPopupRef,
    chatPopupPosition,
    chatPopupSize,
    setIsChatPopupOpen,
    setChatPopupRef,
    setChatPopupPosition,
    setChatPopupSize
  } = useChatPopupStore();
  const {
    images: imagesOfMessage,
    imageIndex: indexOfImages,
    messageMetaInfo: metaInfoOfImageMessage,
    resetStore: resetImageMessageStore
  } = useChatImageMessageStore();
  const chatPopupRef = useRef(null);
  const {
    width: windowWidth
  } = useWindowSize();
  const isTablet = 800 < windowWidth && windowWidth < 1350;
  const [selectedChannelCustomType, setSelectedChannelCustomType] = useState(GroupChannelCustomType.IN_CLASS);
  const [channelUrlsIncludeSearchMember, setChannelUrlsIncludeSearchMember] = useState(null);
  const [isOpenMemberSearchInput, setIsOpenMemberSearchInput] = useState(false);
  const [memberSearchInputValue, setMemberSearchInputValue] = useState(null);
  const [isOpenScheduledMessagePopup, setIsOpenScheduledMessagePopup] = useState(false);
  const [isOpenScheduledMessageListPopup, setIsOpenScheduledMessageListPopup] = useState(false);
  const {
    data: sendbirdUserInfo
  } = useQuery(['GET_SENDBIRD_USER_INFO', user?.userId], () => getSendbirdUserInfo(user?.userId), {
    enabled: !_.isNil(user?.userId)
  });
  const sendbirdUserId = sendbirdUserInfo?.userId;
  const sendbirdAccessToken = sendbirdUserInfo?.accessToken;
  useEffect(() => {
    if (sendbirdUserInfo && isChatPopupOpen && user?.type === UserType.STUDENT) {
      setSelectedChannel({
        url: sendbirdUserInfo.currentClassChannelUrl,
        studentName: user.name
      });
    }
  }, [sendbirdUserInfo, isChatPopupOpen, user]);
  const closeMemberSearchInput = () => {
    setIsOpenMemberSearchInput(null);
    setMemberSearchInputValue(null);
    setChannelUrlsIncludeSearchMember(null);
  };
  useEffect(() => {
    if (_.isNil(user)) {
      setIsChatPopupOpen(false);
      setSelectedChannel(null);
      setSelectedChannelCustomType(null);
      closeMemberSearchInput();
      setChatPopupPosition({
        x: null,
        y: null
      });
      setChatPopupSize({
        width: null,
        height: null
      });
      setIsOpenScheduledMessagePopup(false);
      setIsOpenScheduledMessageListPopup(false);
    }
  }, [user]);
  useKeyboardVisible(isChatPopupOpen, initialInnerHeight => {
    if (chatPopupRef && chatPopupRef.current) {
      const keyboardHeight = initialInnerHeight - window.visualViewport.height;
      const newTop = initialInnerHeight - (keyboardHeight + chatPopupRef.current.querySelector(`.${RESIZABLE_CHAT_ELEMENT_CLASSNAME}`).clientHeight + 10);
      chatPopupRef.current.style.top = `${newTop}px`;
    }
  }, () => {
    if (chatPopupRef && chatPopupRef.current) {
      chatPopupRef.current.style.top = `10px`;
    }
  });
  const calculatePopupPosition = (data, popupRef, axis) => {
    const popupPositionInWindow = axis === 'top' ? popupRef.current?.offsetTop || 0 : popupRef.current?.offsetLeft || 0;
    const mousePositionInWindow = axis === 'top' ? data.y : data.x;
    const mousePositionInPopup = mousePositionInWindow - popupPositionInWindow;
    const delta = mousePositionInWindow - mousePositionInPopup + (axis === 'top' ? data.deltaY : data.deltaX);
    const maxEnableDelta = axis === 'top' ? window.innerHeight - (popupRef.current?.clientHeight || 0) : window.innerWidth - (popupRef.current?.clientWidth || 0);
    const popupFinalPositionInWindow = delta > 0 ? delta > maxEnableDelta ? maxEnableDelta : delta : 0;
    return popupFinalPositionInWindow;
  };
  const handleDragPopup = (e, data, popupRef) => {
    const popupFinalTopInWindow = calculatePopupPosition(data, popupRef, 'top');
    const popupFinalLeftInWindow = calculatePopupPosition(data, popupRef, 'left');
    if (popupRef.current) {
      popupRef.current.style.top = `${popupFinalTopInWindow}px`;
      popupRef.current.style.left = `${popupFinalLeftInWindow}px`;
    }
  };
  const handleDragChatRoom = (e, data) => {
    if (chatPopupRef && chatPopupRef.current) {
      handleDragPopup(e, data, storeChatPopupRef);
    }
  };
  const handleDragStop = (e, data) => {
    const popupFinalTopInWindow = calculatePopupPosition(data, chatPopupRef, 'top');
    const popupFinalLeftInWindow = calculatePopupPosition(data, chatPopupRef, 'left');
    setChatPopupPosition({
      x: popupFinalLeftInWindow,
      y: popupFinalTopInWindow
    });
  };
  useEffect(() => {
    setChatPopupRef(chatPopupRef);
  }, []);
  useEffect(() => {
    if (!isChatPopupOpen) {
      resetImageMessageStore();
    }
  }, [isChatPopupOpen]);
  if (!_.isNil(sendbirdUserId) && !_.isNil(sendbirdAccessToken) && isChatPopupOpen) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(S.ChatGlobalStyle, {}), /*#__PURE__*/_jsx(SendbirdProvider, {
        appId: SEND_BIRD_APP_ID,
        userId: sendbirdUserId,
        accessToken: sendbirdAccessToken,
        stringSet: customStringSet,
        colorSet: {
          '--sendbird-light-primary-100': 'var(--blue-98)',
          '--sendbird-light-primary-300': 'var(--blue-50)',
          '--sendbird-light-primary-400': 'var(--blue-40)',
          '--sendbird-light-primary-500': 'var(--blue-30)',
          '--sendbird-light-secondary-300': 'var(--blue-50)'
        },
        isMultipleFilesMessageEnabled: true,
        sdkInitParams: {
          localCacheEnabled: false
        },
        children: /*#__PURE__*/_jsx(ChatPopupWrapper, {
          draggableCoreProps: {
            onDrag: handleDragChatRoom,
            onStop: handleDragStop
          },
          defaultPosition: {
            x: !_.isNil(chatPopupPosition.x) ? `${chatPopupPosition.x}px` : null,
            y: !_.isNil(chatPopupPosition.y) ? `${chatPopupPosition.y}px` : null
          },
          popupRef: chatPopupRef,
          rndProps: {
            onResizeStop: () => {
              if (!isTablet) {
                const resizableElement = chatPopupRef.current.querySelector(`.${RESIZABLE_CHAT_ELEMENT_CLASSNAME}`);
                setChatPopupSize({
                  width: resizableElement.clientWidth,
                  height: resizableElement.clientHeight
                });
              }
            },
            enableResizing: {
              right: !isTablet,
              bottom: !isTablet,
              bottomRight: !isTablet
            },
            default: {
              x: null,
              y: null,
              width: chatPopupSize.width ?? CHAT_ROOM_REM_WIDTH * 16,
              height: chatPopupSize.height ?? CHAT_ROOM_REM_WIDTH * 16
            }
          },
          children: _.isNil(selectedChannel?.url) ? user.type === UserType.TEACHER ? /*#__PURE__*/_jsx("section", {
            className: "flex w-full h-full",
            children: /*#__PURE__*/_jsx(GroupChannelListProvider, {
              onChannelSelect: async channel => {
                setSelectedChannel({
                  url: channel.url,
                  studentName: getUsernameFromNickname(channel.members.find(member => member.nickname.startsWith('[S]')).nickname)
                });
              },
              onChannelCreated: () => null,
              disableAutoSelect: true,
              channelListQueryParams: {
                hiddenChannelFilter: HiddenChannelFilter.UNHIDDEN,
                customTypesFilter: !clickedChatNotification ? selectedChannelCustomType ? [selectedChannelCustomType] : null : null,
                channelUrlsFilter: channelUrlsIncludeSearchMember
              },
              children: /*#__PURE__*/_jsx(CustomGroupChannelList, {
                selectedChannelCustomType: selectedChannelCustomType,
                isOpenSearchInput: isOpenMemberSearchInput,
                memberSearchInputValue: memberSearchInputValue,
                channelUrlsIncludeSearchMember: channelUrlsIncludeSearchMember,
                onSelectChannelCustomType: setSelectedChannelCustomType,
                toggleSearchInputVisible: visible => {
                  setIsOpenMemberSearchInput(visible);
                  if (!visible) {
                    closeMemberSearchInput();
                  }
                },
                onSearch: async value => {
                  setMemberSearchInputValue(value);
                  const pickedChannelUrls = (await getGroupChannelsUrlContainsStudentName(value)).data.map(channel => channel.channelUrl);
                  setChannelUrlsIncludeSearchMember(pickedChannelUrls);
                }
              })
            })
          }) : /*#__PURE__*/_jsx(CustomGroupChannelListSkeleton, {}) : /*#__PURE__*/_jsx("section", {
            className: "flex min-h-[1px] flex-grow w-full",
            children: /*#__PURE__*/_jsx(GroupChannelProvider, {
              channelUrl: selectedChannel.url,
              children: /*#__PURE__*/_jsx(CustomGroupChannel, {
                onCloseGroupChannel: () => {
                  setSelectedChannel(null);
                  setIsOpenScheduledMessageListPopup(false);
                  setIsOpenScheduledMessagePopup(false);
                },
                openScheduledMessageFormPopup: () => setIsOpenScheduledMessagePopup(true)
              })
            })
          })
        })
      }), (isOpenScheduledMessagePopup || isOpenScheduledMessageListPopup) && /*#__PURE__*/_jsx(ScheduledMessageSection, {
        isOpenScheduledMessagePopup: isOpenScheduledMessagePopup,
        isOpenScheduledMessageListPopup: isOpenScheduledMessageListPopup,
        isTablet: isTablet,
        setIsOpenScheduledMessagePopup: setIsOpenScheduledMessagePopup,
        setIsOpenScheduledMessageListPopup: setIsOpenScheduledMessageListPopup,
        handleDragPopup: handleDragPopup
      }), !_.isNil(imagesOfMessage) && imagesOfMessage.length > 0 && /*#__PURE__*/_jsx(RndMediaWindow, {
        title: `${metaInfoOfImageMessage.sendDate.toFormat('yyyy.MM.dd') ?? ''}`,
        medias: imagesOfMessage,
        size: {
          width: 16 * 49,
          height: 16 * 40
        },
        onClose: resetImageMessageStore,
        initialMediaIndex: indexOfImages
      })]
    });
  } else {
    /*#__PURE__*/_jsx(_Fragment, {});
  }
}
export default SendbirdWrapper;